import { type PermissionResource, type PermissionAction } from '@/services/permission-service';

// Define navigation item structure
export interface NavigationItem {
  id: string; // Unique identifier for icon mapping (i18n-safe)
  title: string;
  url: string;
  icon?: any; // Icon component
  isActive?: boolean; // Whether the item is currently active
  items?: NavigationItem[]; // Sub-items
  permissions?: {
    resource: PermissionResource;
    actions: PermissionAction[];
  };
  visible?: boolean; // Whether the item is visible by default
  badge?: {
    count?: number;
    variant?: 'default' | 'destructive' | 'success' | 'warning';
    showDot?: boolean;
  }; // Badge indicator for notifications
}

/**
 * NavigationDataService
 *
 * This service PROVIDES navigation data, it doesn't filter it.
 * The data returned is what the backend would return - already filtered by permissions.
 *
 * Current: Uses mock data that simulates backend behavior
 * Future: Will call actual API endpoint that returns user-specific navigation
 */
class NavigationDataService {
  /**
   * Get navigation menu for the current user
   * This is the main public API that components should use
   *
   * @param isAdmin - Whether the current user is an admin
   * @param organizationId - Optional organization context
   * @returns Promise<NavigationItem[]> - Navigation items for this user
   */
  async getNavigationMenu(isAdmin: boolean, organizationId?: string): Promise<NavigationItem[]> {
    // TODO: When backend is ready, replace with:
    // return await this.fetchNavigationFromAPI(isAdmin, organizationId);

    // For now, return mock data that simulates what backend would return
    return this.getMockNavigation(isAdmin, organizationId);
  }

  /**
   * Mock navigation data - simulates what the backend would return
   * Backend would filter based on user permissions server-side
   *
   * @param isAdmin - Whether user has admin permissions
   * @param organizationId - Optional organization context for Phase 2
   * @returns NavigationItem[] - Pre-filtered navigation for this user
   */
  private getMockNavigation(isAdmin: boolean, organizationId?: string): NavigationItem[] {
    // Base navigation items that all users see
    const baseNavigation: NavigationItem[] = [
      {
        id: "dashboard",
        title: "Dashboard",
        url: "#",
        items: [
          {
            id: "dashboard-home",
            title: "Home",
            url: "/console/dashboard",
          },
        ],
      },

      {
        id: "kiosks",
        title: "Kiosks",
        url: "#",
        items: [
          {
            id: "kiosks-view",
            title: "View Deployments",
            url: "/console/kiosks",
          },
          {
            id: "kiosks-create",
            title: "Create New Deployment",
            url: "/console/kiosks/new",
          },
        ],
      },
      {
        id: "my-assets",
        title: "My Assets",
        url: "#",
        items: [
          {
            id: "my-assets-browse",
            title: "Browse Assets",
            url: "/console/assets",
          },
        ],
      },
      {
        id: "services",
        title: "Services",
        url: "#",
        items: [
          {
            id: "services-browse",
            title: "Browse Services",
            url: "/console/services",
          },
        ],
      },
      {
        id: "organizations",
        title: "Organizations",
        url: "#",
        items: [
          {
            id: "organizations-overview",
            title: "Overview",
            url: "/console/organizations",
          },
          {
            id: "organizations-members",
            title: "Members",
            url: "/console/organizations/members",
          },
          {
            id: "organizations-shared-assets",
            title: "Shared Assets",
            url: "/console/organizations/assets",
          },
          {
            id: "organizations-invitations",
            title: "Invitations",
            url: "/console/organizations/invitations",
          },
        ],
      },
      {
        id: "settings",
        title: "Settings",
        url: "#",
        items: [
          {
            id: "settings-api-keys",
            title: "API Keys",
            url: "/console/api-keys",
          },
          {
            id: "settings-sessions",
            title: "Active Sessions",
            url: "/console/sessions",
          },
          {
            id: "settings-devices",
            title: "Device Management",
            url: "/console/devices",
          },
          {
            id: "settings-webrtc-controller",
            title: "WebRTC Controller",
            url: "/console/webrtc-controller",
          },
        ],
      },
    ];

    // Admin-only navigation items
    const adminNavigation: NavigationItem[] = [
      {
        id: "admin",
        title: "Admin",
        url: "#",
        items: [
          {
            id: "admin-dashboard",
            title: "Dashboard",
            url: "/console/admin",
          },
          {
            id: "admin-users",
            title: "User Management",
            url: "/console/admin/users",
          },
          {
            id: "admin-roles",
            title: "Role Management",
            url: "/console/admin/roles",
          },
          {
            id: "admin-sessions",
            title: "Session Management",
            url: "/console/admin/sessions",
          },
        ],
      },
    ];

    // Backend simulation: Return different data based on user permissions
    // In real implementation, backend does this filtering
    if (isAdmin) {
      return [...baseNavigation, ...adminNavigation];
    }

    return baseNavigation;
  }

  /**
   * Future: Fetch navigation from backend API
   * This will replace getMockNavigation() when backend is ready
   *
   * @param isAdmin - Whether user has admin permissions
   * @param organizationId - Optional organization context
   * @returns Promise<NavigationItem[]> - Navigation from API
   */
  private async fetchNavigationFromAPI(isAdmin: boolean, organizationId?: string): Promise<NavigationItem[]> {
    // TODO: Implement when backend API is ready
    // const response = await fetch('/api/navigation', {
    //   method: 'GET',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   // Backend will know user from session/cookie
    // });
    //
    // if (!response.ok) {
    //   throw new Error('Failed to fetch navigation');
    // }
    //
    // return await response.json();

    // Placeholder - falls back to mock
    return this.getMockNavigation(isAdmin, organizationId);
  }
}

// Create singleton instance
export const navigationDataService = new NavigationDataService();
